import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import * as styles from './StudyListSidebar.module.scss';
import { useStudyListLayout } from 'pages/StudyList/StudyListLayoutContext';

const compactSidebarGap = 20;
const compactStudyListInset = 20;
const desktopSidebarMinGap = 40;
const wideDesktopMaxGap = 64;
const compactSidebarReferenceViewport = 800;
const desktopSidebarReferenceViewport = 1024;
const singleColumnMinViewport = 1440;
const singleColumnMaxViewport = 1959;
const twoColumnMinViewport = 1960;
const wideDesktopReferenceViewport = 2560;
const singleColumnMinWidth = 320;
const singleColumnMaxWidth = 516;
const twoColumnMinWidth = 517;

const getStudyListMessageRoots = () =>
  Array.from(
    document.querySelectorAll<HTMLElement>('[data="studyListMessageContainer"]')
  );

const getSidebarTop = (secondaryNavigationRoot: HTMLElement) =>
  Math.max(0, secondaryNavigationRoot.getBoundingClientRect().bottom);

const getSidebarGap = (viewportWidth: number) => {
  if (viewportWidth < desktopSidebarReferenceViewport) {
    const progress = Math.max(
      0,
      Math.min(
        1,
        (viewportWidth - compactSidebarReferenceViewport) /
          (desktopSidebarReferenceViewport - compactSidebarReferenceViewport)
      )
    );

    return (
      compactSidebarGap + progress * (desktopSidebarMinGap - compactSidebarGap)
    );
  }

  const progress = Math.max(
    0,
    Math.min(
      1,
      (viewportWidth - twoColumnMinViewport) /
        (wideDesktopReferenceViewport - twoColumnMinViewport)
    )
  );

  return (
    desktopSidebarMinGap + progress * (wideDesktopMaxGap - desktopSidebarMinGap)
  );
};

const getStudyListLeft = (viewportWidth: number, layoutLeft: number) => {
  if (viewportWidth >= desktopSidebarReferenceViewport) return layoutLeft;

  const compactLeft = Math.min(layoutLeft, compactStudyListInset);
  const progress = Math.max(
    0,
    Math.min(
      1,
      (viewportWidth - compactSidebarReferenceViewport) /
        (desktopSidebarReferenceViewport - compactSidebarReferenceViewport)
    )
  );

  return compactLeft + progress * (layoutLeft - compactLeft);
};

const getSidebarWidth = (
  viewportWidth: number,
  layoutWidth: number,
  studyListRight: number,
  sidebarGap: number
) => {
  if (viewportWidth <= singleColumnMaxViewport) {
    const progress = Math.max(
      0,
      Math.min(
        1,
        (viewportWidth - singleColumnMinViewport) /
          (singleColumnMaxViewport - singleColumnMinViewport)
      )
    );

    return (
      singleColumnMinWidth +
      progress * (singleColumnMaxWidth - singleColumnMinWidth)
    );
  }

  return Math.max(twoColumnMinWidth, layoutWidth - studyListRight - sidebarGap);
};

type Options = {
  isDocked: boolean; // False while the sidebar is presented as a modal sheet, which leaves the study list at its full width
  isOpen: boolean;
};

// Sizes a docked sidebar against the study list and contracts the list, its
// toolbar and any list-level messages to make room for it.
export const useStudyListSidebarLayout = ({ isDocked, isOpen }: Options) => {
  const panelRef = useRef<HTMLElement>(null);
  const scrollFrameRef = useRef<number | null>(null);
  const [position, setPosition] = useState<{ top: number; width: number }>();
  const {
    toolbarRef,
    listRef,
    subHeaderRef,
    pageContentRef,
  } = useStudyListLayout();

  const updatePosition = useCallback(() => {
    const studyListRoot = listRef.current;
    const secondaryNavigationRoot = subHeaderRef.current;
    const toolbarRoot = toolbarRef.current;

    if (!studyListRoot || !secondaryNavigationRoot) return;

    // The list itself contracts while the sidebar is open, so measure its
    // unchanged layout parent to avoid compounding the resize.
    const studyListLayoutRoot = pageContentRef.current || studyListRoot;
    const { left, right } = studyListLayoutRoot.getBoundingClientRect();
    const layoutWidth =
      document.documentElement.clientWidth || window.innerWidth;
    const sidebarGap = getSidebarGap(window.innerWidth);
    const studyListLeft = getStudyListLeft(window.innerWidth, left);
    const sidebarWidth = getSidebarWidth(
      window.innerWidth,
      layoutWidth,
      right,
      sidebarGap
    );
    const studyListWidth = Math.max(
      0,
      layoutWidth - sidebarWidth - sidebarGap - studyListLeft
    );
    const top = getSidebarTop(secondaryNavigationRoot);
    setPosition({ top, width: sidebarWidth });

    if (isOpen) {
      [toolbarRoot, studyListRoot].forEach((element) => {
        element?.classList.add(styles.SidebarOpen);
        element?.style.setProperty(
          '--study-list-sidebar-list-width',
          `${studyListWidth}px`
        );
        element?.style.setProperty(
          '--study-list-sidebar-list-offset',
          `${studyListLeft - left}px`
        );
      });

      getStudyListMessageRoots().forEach((element) => {
        const computedStyle = window.getComputedStyle(element);
        const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
        const paddingRight = parseFloat(computedStyle.paddingRight) || 0;

        element.classList.add(styles.SidebarMessageOpen);
        element.style.setProperty(
          '--study-list-sidebar-message-width',
          `${studyListWidth + paddingLeft + paddingRight}px`
        );
        element.style.setProperty(
          '--study-list-sidebar-message-left',
          `${Math.max(0, studyListLeft - paddingLeft)}px`
        );
      });
    }
  }, [isOpen, listRef, pageContentRef, subHeaderRef, toolbarRef]);

  const updateTop = useCallback(() => {
    const secondaryNavigationRoot = subHeaderRef.current;

    if (!panelRef.current || !secondaryNavigationRoot) return;

    const top = getSidebarTop(secondaryNavigationRoot);
    panelRef.current.style.top = `${top}px`;
  }, [subHeaderRef]);

  useLayoutEffect(() => {
    if (!isDocked) return;

    const resizeTargets = [
      toolbarRef.current,
      listRef.current,
      ...getStudyListMessageRoots(),
    ].filter((element): element is HTMLElement => Boolean(element));

    resizeTargets.forEach((element) => {
      element.classList.add(styles.SidebarResizeTarget);
    });

    return () => {
      resizeTargets.forEach((element) => {
        element.classList.remove(
          styles.SidebarResizeTarget,
          styles.SidebarOpen,
          styles.SidebarMessageOpen
        );
        element.style.removeProperty('--study-list-sidebar-list-width');
        element.style.removeProperty('--study-list-sidebar-list-offset');
        element.style.removeProperty('--study-list-sidebar-message-width');
        element.style.removeProperty('--study-list-sidebar-message-left');
      });
    };
  }, [isDocked, listRef, toolbarRef]);

  useLayoutEffect(() => {
    if (!isOpen || !isDocked) return;

    const studyListRoot = listRef.current;
    const secondaryNavigationRoot = subHeaderRef.current;
    const toolbarRoot = toolbarRef.current;
    const resizeObserver = new ResizeObserver(updatePosition);
    const handleScroll = () => {
      if (scrollFrameRef.current !== null) return;

      scrollFrameRef.current = window.requestAnimationFrame(() => {
        scrollFrameRef.current = null;
        updateTop();
      });
    };

    updatePosition();
    if (studyListRoot) resizeObserver.observe(studyListRoot);
    if (secondaryNavigationRoot) {
      resizeObserver.observe(secondaryNavigationRoot);
    }
    if (toolbarRoot) resizeObserver.observe(toolbarRoot);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', handleScroll);
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
        scrollFrameRef.current = null;
      }

      [toolbarRoot, studyListRoot, ...getStudyListMessageRoots()].forEach(
        (element) => {
          element?.classList.remove(styles.SidebarOpen);
          element?.classList.remove(styles.SidebarMessageOpen);
          element?.style.removeProperty('--study-list-sidebar-list-width');
          element?.style.removeProperty('--study-list-sidebar-list-offset');
          element?.style.removeProperty('--study-list-sidebar-message-width');
          element?.style.removeProperty('--study-list-sidebar-message-left');
        }
      );
    };
  }, [
    isOpen,
    isDocked,
    listRef,
    subHeaderRef,
    toolbarRef,
    updatePosition,
    updateTop,
  ]);

  return {
    panelRef,
    panelStyle: position
      ? { top: `${position.top}px`, width: `${position.width}px` }
      : undefined,
  };
};
